import React from 'react';
import { Box, Typography, Chip, IconButton, Avatar } from '@mui/material';
import { AccessTime, Person, Business } from '@mui/icons-material';
import { Trash2, Copy } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from 'react-hot-toast';
import EllipsisMenu from '../Layout/EllipsisMenu';
import { successToastConfig, brandColors } from '../../utils/data';

const Invite = ({ invite, onAccept, onDecline, onDelete }) => {
  const getTimeAgo = (date) => {
    try {
      return formatDistanceToNow(new Date(date), { addPrefix: false, addSuffix: true, includeSeconds: false });
    } catch (error) {
      return 'Unknown time';
    }
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'superadmin':
        return '#d32f2f';
      case 'admin':
        return '#1976d2';
      case 'user':
        return '#388e3c';
      default:
        return '#757575';
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'organization':
        return <Business fontSize="small" />;
      case 'user':
      default:
        return <Person fontSize="small" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'organization':
        return brandColors.orange;
      case 'user':
      default:
        return brandColors.blue;
    }
  };

  const handleDelete = () => {
        if (onDelete) {
      onDelete(invite);
    }
  };

  const getStatus = (acceptedAt) => {
    if(acceptedAt === null) {
      return 'Pending';
    }
    return 'Accepted';

  };

  const getStatusColor = (acceptedAt) => {
    if(acceptedAt === null) {
      return brandColors.amberYellow;
    }
    return brandColors.mintGreen;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        mb: 1,
        borderRadius: 1,
        maxWidth: 420,
        backgroundColor: 'var(--bg-panel)',
        border: '1px solid var(--border-color)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: 'var(--bg-panel)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
        position: 'relative',
      }}
    >
      <EllipsisMenu 
        onDelete={handleDelete}
        showShare={false}
        size={16}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            mr: 2,
            backgroundColor: getTypeColor(invite.type),
            color: 'white',
          }}
        >
          {getTypeIcon(invite.type)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'var(--text-main, #222)',
                mr: 1,
              }}
            >
              {invite.email}
            </Typography>
            <Chip
                label={getStatus(invite.acceptedAt)}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.7rem',
                  backgroundColor: getStatusColor(invite.acceptedAt),
                  color: 'white',
                  fontWeight: 500,
                }}
              />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={invite.role}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.7rem',
                backgroundColor: getRoleColor(invite.role),
                color: 'white',
                fontWeight: 500,
              }}
            />
            {invite.code && (
              <CopyToClipboard 
                text={invite.code}
                onCopy={() => toast('Link copied.', successToastConfig)}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'var(--text-secondary, #555)',
                    fontFamily: 'monospace',
                    backgroundColor: 'var(--bg-hover, rgba(255,255,255,0.05))',
                    px: 1,
                    py: 0.25,
                    borderRadius: '5px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'var(--bg-hover)',
                      color: 'var(--text-primary)',
                    },
                  }}
                >
                  <Typography variant="caption">
                    {invite.code}
                  </Typography>
                  <Copy size={12} />
                </Box>
              </CopyToClipboard>
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
       
        {/* {onDelete && (
          <IconButton
            size="small"
            onClick={() => onDelete(invite)}
            sx={{
              color: 'var(--text-tertiary, #888)',
              '&:hover': {
                backgroundColor: '#f44336',
                color: 'white',
              },
            }}
          >
            <Trash2 size={16} />
          </IconButton>
        )} */}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: 4,
          right: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
        }}
      >
        <AccessTime
          sx={{
            fontSize: '11px',
            color: 'var(--text-tertiary, #888)',
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontSize: '11px',
            color: 'var(--text-tertiary, #888)',
            fontFamily: 'monospace',
          }}
        >
          {getTimeAgo(invite.created)}
        </Typography>
      </Box>
    </Box>
  );
};

export default Invite; 